const BASE_URL = "https://nagrik.thecanatech.com/";

// Helper to check if a JWT token is expired
export const isTokenExpired = (token) => {
  if (!token || typeof token !== "string") return true;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const base64Url = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const base64 = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), "=");
    const payloadJson = atob(base64);
    const payload = JSON.parse(payloadJson);
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch (e) {
    return false;
  }
};

// Helper to redirect to login page when token is invalid or expired
export const redirectToLogin = () => {
  if (typeof window === "undefined") return;

  const currentPath = window.location.pathname;
  if (
    currentPath === "/authentication/sign-in" ||
    currentPath === "/login" ||
    currentPath.startsWith("/admin/login") ||
    currentPath.startsWith("/admin/signin") ||
    currentPath.startsWith("/signin")
  ) {
    return;
  }

  // Clear authentication cache
  localStorage.clear();

  const isCitizenRoute =
    currentPath === "/" ||
    currentPath.startsWith("/complaint") ||
    currentPath.startsWith("/about_us") ||
    currentPath.startsWith("/schemes") ||
    currentPath.startsWith("/gallery") ||
    currentPath.startsWith("/janta-darbar") ||
    currentPath.startsWith("/all_user_complaints") ||
    currentPath.startsWith("/user_complaints") ||
    currentPath.startsWith("/profile");

  const target = isCitizenRoute ? "/login" : "/authentication/sign-in";
  window.location.href = target;
};

// Setup global fetch interceptor to catch any 401 across all raw fetch calls
if (typeof window !== "undefined" && !window.__nagrik_fetch_interceptor_installed__) {
  window.__nagrik_fetch_interceptor_installed__ = true;
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (response.status === 401) {
      const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
      if (!url.includes("auth/login") && !url.includes("user/login")) {
        redirectToLogin();
      }
    }
    return response;
  };
}

export const apiFetch = async (endpoint, options = {}) => {
  const token = localStorage.getItem("token") || localStorage.getItem("user_token");

  // If token exists and is already expired locally, redirect immediately
  if (token && isTokenExpired(token)) {
    redirectToLogin();
    throw new Error("Session expired. Redirecting to login...");
  }

  const isFormData = options.body instanceof FormData;

  const headers = {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...options.headers,
  };

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized for protected endpoints
  if (response.status === 401 && !endpoint.includes("auth/login") && !endpoint.includes("user/login")) {
    redirectToLogin();
    throw new Error("Invalid or expired token. Redirecting to login...");
  }

  const data = await response.json();

  if (!response.ok) {
    const msg = (data.message || "").toLowerCase();
    if (
      msg.includes("invalid token") ||
      msg.includes("token expired") ||
      msg.includes("jwt expired") ||
      msg.includes("no token") ||
      msg.includes("access denied")
    ) {
      if (!endpoint.includes("auth/login") && !endpoint.includes("user/login")) {
        redirectToLogin();
      }
    }
    throw new Error(data.message || "API error");
  }

  // Handle cases where response is 200 but backend body indicates failure with invalid token
  if (data && data.success === false) {
    const msg = (data.message || "").toLowerCase();
    if (
      msg.includes("invalid token") ||
      msg.includes("token expired") ||
      msg.includes("jwt expired") ||
      msg.includes("access denied. please login")
    ) {
      redirectToLogin();
      throw new Error(data.message || "Invalid or expired token");
    }
  }

  return data;
};

// Robust Date Formatter for HTML Date Input (YYYY-MM-DD) avoiding UTC timezone shifts
export const formatDateForInput = (dateVal) => {
  if (!dateVal) return "";

  // If already string in YYYY-MM-DD format (no T offset component)
  if (typeof dateVal === "string" && !dateVal.includes("T")) {
    return dateVal.split(" ")[0];
  }

  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "";

  // Format in Local Timezone YYYY-MM-DD instead of UTC toISOString()
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export default BASE_URL;